import { memo, useEffect } from 'react';
import { Divider, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DeviceWrapper, Flex } from '@/components';
import { useModal, useSingleSelfRedirect } from '@/hooks';
import { useAppContext } from '@/context';
import { useSelfContext } from '@/context/SelfContext';
import {
  SpoonForkSVG,
  BallStrokeSVG,
  DoctorSVG,
  BusSVG,
} from '@/components/common/AppIcons';
import SelfListModal from '../../modals/SelfListModal';
import { envConfig } from '@/configs';
import { LuChevronLeft } from 'react-icons/lu';

const getSelfIcon = (variant: ComponentVariant) =>
({
  sport: BallStrokeSVG,
  nurture: SpoonForkSVG,
  health: DoctorSVG,
  transport: BusSVG,
}[variant]);

export interface ReserveHeaderProps {
  mobileButton?: any;
  modalDefaultVisible?: boolean;
  isNewReserveMode: boolean;
  setIsNewReserveMode: (value: boolean) => void;
}

export const ReserveHeader = memo(({ mobileButton, modalDefaultVisible = true, isNewReserveMode = false, setIsNewReserveMode }: ReserveHeaderProps) => {

  const modal = useModal();
  const navigate = useNavigate();
  const [t] = useTranslation(['common']);
  const { mode } = useAppContext();
  const { currentSelf, selfText, redirectToSelf } = useSelfContext();

  useEffect(() => {
    if (!currentSelf && modalDefaultVisible) modal.show();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalDefaultVisible]);

  useSingleSelfRedirect();

  const prefix = selfText('choose-self-prefix');

  const SelfIconSVG = getSelfIcon(mode);
  const conf = envConfig.onPremise;

  return (
    <>
      <DeviceWrapper show={['desktop']}>
        <SelfListModal {...modal} onSelect={modal.hide} />
        <Flex
          direction="row"
          align="center"
          justify="center"
          className="w-[60%] mx-auto mb-0 mb:lg-0"
        >
          <SelfIconSVG width={30} height={30} />
          <div
            onClick={modal.show}
            className={'flex-center p-2 rounded-lg border-[0.5px] border-b3 cursor-pointer w-40 my-2'}
          >
            <span className="flex-fill text-start mx-2">
              {currentSelf?.name || t('common:messages.choose-your-desired-typeof', { value: prefix })}
            </span>
            <LuChevronLeft className="text-black/75 text-2xl" />
          </div>
        </Flex>
        {conf.core.changableReserveMode === true && (
            <Select
              size="middle"
              className="h-11 me-2"
              value={isNewReserveMode ? "true" : "false"}
              onChange={value => setIsNewReserveMode(value === "true")}
              placeholder={t('reserve:labels.reserve-mode')}
              options={[
                { label: t('reserve:labels.default-reserve-mode'), value: "false" },
                { label: t('reserve:labels.new-reserve-mode'), value: "true" },
              ]}
            />
          )
        }
      </DeviceWrapper>
      <DeviceWrapper show={['mobile']}>
        <Flex className="w-full px-4" direction="column" noGap>
          <Flex
            direction="row"
            align="center"
            justify="center"
            className="w-full min-h-[50px] mx-auto"
          >
            <SelfIconSVG width={35} height={35} />
            <div
              onClick={() => redirectToSelf({ mode, navigate })}
              className="flex items-center justify-center p-1.5 rounded-lg border-[0.5px] border-b3 cursor-pointer w-full my-2"
            >
              <span className="flex-fill text-start mx-2 text-sm">
                {currentSelf?.name || t('common:messages.choose-your-desired-typeof', { value: prefix })}
              </span>
              <LuChevronLeft className="text-black/75 text-xl" />
            </div>
            <span className="ms-auto">{mobileButton}</span>
          </Flex>
          {
            conf.core.changableReserveMode === true && (
              <Select
                size="middle"
                className="text-sm"
                value={isNewReserveMode ? "true" : "false"}
                onChange={value => setIsNewReserveMode(value === "true")}
                placeholder={t('reserve:labels.reserve-mode')}
                options={[
                  { label: t('reserve:labels.default-reserve-mode'), value: "false" },
                  { label: t('reserve:labels.new-reserve-mode'), value: "true" },
                ]}
              />
            )
          }
          <Divider className="!mt-2" />
        </Flex>
      </DeviceWrapper>
    </>
  );
});

export default ReserveHeader;
